/**
 * Tax-Rates Fixture — mirrors `/v1/admin/tax-rates` Core-Shape exakt.
 * Spec: lmm-wiki/wiki/orbit-specs/done/admin-tax-matrix-editable.md
 */

const EU_DEFAULTS = [
  { country: 'AT', standard: 20,   reduced: 10,   ossEligible: true },
  { country: 'BE', standard: 21,   reduced: 6,    ossEligible: true },
  { country: 'BG', standard: 20,   reduced: 9,    ossEligible: true },
  { country: 'CY', standard: 19,   reduced: 5,    ossEligible: true },
  { country: 'CZ', standard: 21,   reduced: 10,   ossEligible: true },
  { country: 'DE', standard: 19,   reduced: 7,    ossEligible: true },
  { country: 'DK', standard: 25,   reduced: null, ossEligible: true },
  { country: 'EE', standard: 22,   reduced: 9,    ossEligible: true },
  { country: 'ES', standard: 21,   reduced: 10,   ossEligible: true },
  { country: 'FI', standard: 25.5, reduced: 14,   ossEligible: true },
  { country: 'FR', standard: 20,   reduced: 5.5,  ossEligible: true },
  { country: 'GR', standard: 24,   reduced: 13,   ossEligible: true },
  { country: 'HR', standard: 25,   reduced: 5,    ossEligible: true },
  { country: 'HU', standard: 27,   reduced: 5,    ossEligible: true },
  { country: 'IE', standard: 23,   reduced: 9,    ossEligible: true },
  { country: 'IT', standard: 22,   reduced: 4,    ossEligible: true },
  { country: 'LT', standard: 21,   reduced: 5,    ossEligible: true },
  { country: 'LU', standard: 17,   reduced: 3,    ossEligible: true },
  { country: 'LV', standard: 21,   reduced: 5,    ossEligible: true },
  { country: 'MT', standard: 18,   reduced: 5,    ossEligible: true },
  { country: 'NL', standard: 21,   reduced: 9,    ossEligible: true },
  { country: 'PL', standard: 23,   reduced: 5,    ossEligible: true },
  { country: 'PT', standard: 23,   reduced: 6,    ossEligible: true },
  { country: 'RO', standard: 19,   reduced: 5,    ossEligible: true },
  { country: 'SE', standard: 25,   reduced: 6,    ossEligible: true },
  { country: 'SI', standard: 22,   reduced: 5,    ossEligible: true },
  { country: 'SK', standard: 23,   reduced: 5,    ossEligible: true },
];

function persistOverrides() {
  if (typeof window === 'undefined') return null;
  if (!window.__ORBIT_TAX_OVERRIDES_DB__) {
    window.__ORBIT_TAX_OVERRIDES_DB__ = {};
  }
  return window.__ORBIT_TAX_OVERRIDES_DB__;
}

/** EL → GR Normalisierung — Core macht das gleiche serverseitig. */
function normalizeCountry(country) {
  if (country == null) return country;
  const up = String(country).trim().toUpperCase();
  return up === 'EL' ? 'GR' : up;
}

function classifyWarnings(body) {
  const w = [];
  if (body.standard != null && (body.standard < 5 || body.standard > 30)) {
    w.push(`Standard-Rate ${body.standard}% liegt außerhalb des typischen EU-Range (5–30%).`);
  }
  if (body.reduced != null && (body.reduced < 0 || body.reduced > 20)) {
    w.push(`Reduced-Rate ${body.reduced}% liegt außerhalb des typischen Range (0–20%).`);
  }
  return w;
}

function validateHard(body) {
  if (body.standard != null && (body.standard < 0 || body.standard > 50)) {
    return { code: 'rate_out_of_range', message: 'standard außerhalb [0, 50]' };
  }
  if (body.reduced != null && (body.reduced < 0 || body.reduced > 50)) {
    return { code: 'rate_out_of_range', message: 'reduced außerhalb [0, 50]' };
  }
  if (body.reduced != null && body.standard != null && body.reduced > body.standard) {
    return { code: 'rate_out_of_range', message: 'reduced > standard' };
  }
  return null;
}

export function taxRatesFixture() {
  const overrides = persistOverrides() ?? {};
  const seen = new Set();
  const rates = [];

  for (const row of EU_DEFAULTS) {
    const o = overrides[row.country];
    if (o) {
      rates.push({
        country: row.country,
        categorySlug: null,
        standard: o.standard,
        reduced: o.reduced,
        ossEligible: o.ossEligible ?? row.ossEligible,
        source: 'override',
        reason: o.reason ?? null,
        overriddenAt: o.overriddenAt,
        overriddenBy: 42,
        overriddenByEmail: 'ops@valitype.com',
      });
    } else {
      rates.push({
        country: row.country,
        categorySlug: null,
        standard: row.standard,
        reduced: row.reduced,
        ossEligible: row.ossEligible,
        source: 'default',
        reason: null,
        overriddenAt: null,
        overriddenBy: null,
        overriddenByEmail: null,
      });
    }
    seen.add(row.country);
  }

  // Drittland-Overrides die kein EU-Default haben
  for (const [country, o] of Object.entries(overrides)) {
    if (seen.has(country)) continue;
    rates.push({
      country,
      categorySlug: null,
      standard: o.standard,
      reduced: o.reduced,
      ossEligible: o.ossEligible ?? false,
      source: 'override',
      reason: o.reason ?? null,
      overriddenAt: o.overriddenAt,
      overriddenBy: 42,
      overriddenByEmail: 'ops@valitype.com',
    });
  }

  return {
    rates,
    sourceFile: 'src/utils/vatRates.js + tax_rate_overrides table',
    serverStartedAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
  };
}

export function createTaxRateFixture(body) {
  const country = normalizeCountry(body.country);
  if (!country || !/^[A-Z]{2}$/.test(country)) {
    const err = new Error('invalid_country');
    err.data = { code: 'invalid_country', message: 'country muss ISO-2 sein' };
    err.response = { status: 400 };
    throw err;
  }
  const hard = validateHard(body);
  if (hard) {
    const err = new Error(hard.code);
    err.data = hard;
    err.response = { status: 400 };
    throw err;
  }
  const db = persistOverrides();
  if (!db) return { ok: true };
  if (db[country]) {
    const err = new Error('country_already_overridden');
    err.data = { code: 'country_already_overridden', message: `${country} hat bereits einen Override — bitte PUT nutzen` };
    err.response = { status: 409 };
    throw err;
  }
  db[country] = {
    standard: body.standard,
    reduced: body.reduced ?? null,
    ossEligible: body.ossEligible ?? false,
    reason: body.reason,
    overriddenAt: new Date().toISOString(),
  };
  return { ok: true, country, warnings: classifyWarnings(body) };
}

export function updateTaxRateFixture(country, body) {
  const c = normalizeCountry(country);
  const hard = validateHard(body);
  if (hard) {
    const err = new Error(hard.code);
    err.data = hard;
    err.response = { status: 400 };
    throw err;
  }
  const db = persistOverrides();
  if (!db) return { ok: true };
  db[c] = {
    standard: body.standard,
    reduced: body.reduced ?? null,
    ossEligible: body.ossEligible ?? db[c]?.ossEligible ?? false,
    reason: body.reason,
    overriddenAt: new Date().toISOString(),
  };
  return { ok: true, country: c, warnings: classifyWarnings(body) };
}

export function deleteTaxRateFixture(country) {
  const c = normalizeCountry(country);
  const db = persistOverrides();
  const hadDefault = EU_DEFAULTS.some((r) => r.country === c);
  if (db) delete db[c];
  return { ok: true, fallback: hadDefault ? 'default' : 'missing' };
}
